/**
 * The media library — `/v1/admin/media`.
 *
 * **The boundary is the server's, and this module does not soften it.** The library covers the
 * `catalog` and `pandit` prefixes; asking it for `evidence` or `kyc` is a 422 by declaration, and
 * the screen surfaces that refusal verbatim rather than filtering the option away. A console that
 * quietly hid the choice would leave an operator believing there is no evidence to find.
 *
 * **Filters go to the server, never to the client.** The grid is paged, so a filter applied after
 * the fetch would narrow one page of an unknown many — which reads as "3 results" when the answer
 * is "3 on this page".
 *
 * **Preview URLs are minted per object and expire.** They are not cached across a session for
 * that reason: `staleTime` here is deliberately short, and a stale tile refetches rather than
 * showing an expired signature as a broken image.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import { putPresigned } from '../storage';
import { ApiError } from '../client';
import type { components } from '../../../api/generated/schema';

export type MediaLibraryRow = components['schemas']['MediaLibraryRow'];
export type MediaLibraryPage = components['schemas']['MediaLibraryPage'];
export type MediaUsageRow = components['schemas']['MediaUsageRow'];
type MediaUsageList = components['schemas']['MediaUsageList'];
type MediaPreviewUrl = components['schemas']['MediaPreviewUrl'];
export type MediaUsageRef = components['schemas']['MediaUsageRef'];
type MediaReplaceUsagesOut = components['schemas']['MediaReplaceUsagesOut'];

export interface LibraryQuery {
  prefix_class?: string;
  status?: string;
  entity_type?: string;
  entity_id?: number;
  kind?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

/** Only the parts the operator actually set — an empty `q=` would narrow to nothing. */
export function libraryParams(query: LibraryQuery): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  return search.toString();
}

export function useMediaLibrary(query: LibraryQuery) {
  const params = libraryParams(query);
  return useQuery({
    queryKey: keys.media.list(query as Readonly<Record<string, unknown>>),
    queryFn: () =>
      // Always a query string: the screen sets `page` and `page_size` on every call, because
      // an unpaged request to a paged endpoint is a request whose answer size nobody chose.
      authedClient.request<MediaLibraryPage>(`/v1/admin/media?${params}`),
  });
}

export function useMediaUsages(mediaId: number) {
  return useQuery({
    queryKey: keys.media.usages(mediaId),
    queryFn: () => authedClient.request<MediaUsageList>(`/v1/admin/media/${mediaId}/usages`),
  });
}

/**
 * A signed URL for one variant, or for the original while the derivative does not exist yet.
 *
 * The server decides which of those it returned and says so in `variant`; the screen reads that
 * rather than assuming it got what it asked for, because "this is the card crop" and "this is the
 * 8 MB original standing in for it" are different things to tell somebody looking at a grid.
 */
export function useMediaPreview(mediaId: number | null, variant: string) {
  return useQuery({
    queryKey: keys.media.preview(mediaId ?? 0, variant),
    queryFn: () =>
      authedClient.request<MediaPreviewUrl>(
        `/v1/admin/media/${mediaId}/preview?variant=${encodeURIComponent(variant)}`,
      ),
    enabled: mediaId !== null,
    // The signature is short-lived; a cached one outliving it renders as a broken image.
    staleTime: 60_000,
  });
}

/**
 * Re-point the ticked usages at another object — all of them or none (CSR-032).
 *
 * The server refuses a replacement that is not `ready` (409 `media_not_ready`) and a selection
 * somebody has moved since the drawer was read (409 `media_usage_stale`); both arrive as an
 * `ApiError` the screen shows verbatim. Every media query is invalidated afterwards, because the
 * old object's panel, the new one's and the grid all describe the move.
 */
export function useReplaceMediaUsages() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      mediaId,
      newMediaId,
      usages,
    }: {
      mediaId: number;
      newMediaId: number;
      usages: MediaUsageRef[];
    }) =>
      authedClient.request<MediaReplaceUsagesOut>(`/v1/admin/media/${mediaId}/replace-usages`, {
        method: 'POST',
        body: { new_media_id: newMediaId, usages },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.media.all() }),
  });
}

/**
 * Delete an object nothing uses. The row is kept as `deleted` for the audit trail; its bytes
 * are removed from storage. `reason` is required and audited — the server 409s with every usage
 * named if the object turned out to be in use after all.
 */
export function useDeleteMedia() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId, reason }: { mediaId: number; reason: string }) =>
      authedClient.request<void>(`/v1/admin/media/${mediaId}`, {
        method: 'DELETE',
        body: { reason },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.media.all() }),
  });
}

type CatalogMint = components['schemas']['MintOut'];
type CatalogConfirm = components['schemas']['ConfirmOut'];

/** What the catalogue prefix accepts; the server checks again, this only narrows the picker. */
export const CATALOG_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/**
 * One puja image: mint → PUT the bytes straight to storage → confirm, which starts the scan.
 *
 * The PUT is a bare `fetch` — the presigned URL carries its own signature, and our bearer token on
 * it would be a second credential on a request that does not want one. The image is not usable
 * until the scan marks it `ready`; attaching it is then the Media list's job, as for any library
 * image, so an upload never puts an unscanned file on a page.
 */
export function useUploadPujaImage() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ pujaId, file }: { pujaId: number; file: File }) => {
      const minted = await authedClient.request<CatalogMint>('/v1/admin/media/uploads', {
        method: 'POST',
        body: { entity_type: 'puja', entity_id: pujaId, content_type: file.type, declared_bytes: file.size },
      });
      // A catalogue image is under the multipart threshold, so the mint is always a single PUT.
      const left = `Entry #${minted.id} stays pending in the library — delete it there.`;
      try {
        await putPresigned(minted.upload!, file, left);
      } catch (refused) {
        // The minted row points at no bytes, and an admin mint has no owner for the next-mint sweep
        // to clear it by — so clear it here, or it sits in the library as an empty tile for good.
        const cleared = await authedClient
          .request<void>(`/v1/admin/media/${minted.id}`, {
            method: 'DELETE',
            body: { reason: 'The upload never reached storage.' },
          })
          .then(
            () => true,
            () => false,
          );
        if (!cleared) throw refused;
        const error = refused as ApiError;
        throw new ApiError({
          status: error.status,
          code: error.code,
          detail: error.detail.replace(left, 'Nothing was recorded.'),
        });
      }
      return authedClient.request<CatalogConfirm>(`/v1/admin/media/uploads/${minted.id}/confirm`, {
        method: 'POST',
      });
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.media.all() }),
  });
}
